import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { playAlertSound } from "@/lib/alertSounds";

export function useMarketData() {
  const [marketData, setMarketData] = useState({ stocks: [], cryptos: [] });
  const [loading, setLoading] = useState(true);
  const [priceTicks, setPriceTicks] = useState({});
  const prevPricesRef = useRef({});
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);
  const pollIntervalRef = useRef(null);
  // Tracks whether the hook is still mounted; reconnect should bail otherwise.
  const isActiveRef = useRef(true);
  // Exponential backoff (ms) — caps console noise when WS is unreachable.
  const reconnectDelayRef = useRef(5000);
  // After this many consecutive failures we give up on WS and use REST polling.
  const failureCountRef = useRef(0);
  const MAX_WS_FAILURES = 3;

  const fetchMarketData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/overview`);
      setMarketData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching market data:", error);
      setLoading(false);
    }
  }, []);

  const startRestPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(fetchMarketData, 10000);
  }, [fetchMarketData]);

  const connectWebSocket = useCallback(() => {
    if (!isActiveRef.current) return;
    // If we've already given up on WS, just keep polling
    if (failureCountRef.current >= MAX_WS_FAILURES) {
      startRestPolling();
      return;
    }
    try {
      const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/ws/prices`);

      ws.onopen = () => {
        failureCountRef.current = 0;
        reconnectDelayRef.current = 5000;
        // Stop REST polling once WS is healthy
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'market_update') {
            const allAssets = [...(data.stocks || []), ...(data.cryptos || [])];
            const newTicks = {};
            allAssets.forEach(asset => {
              if (asset.tick && asset.tick !== 'none') {
                newTicks[asset.symbol] = asset.tick;
              }
              prevPricesRef.current[asset.symbol] = asset.price;
            });
            if (Object.keys(newTicks).length > 0) {
              setPriceTicks(newTicks);
              setTimeout(() => setPriceTicks({}), 1200);
            }
            setMarketData({ stocks: data.stocks || [], cryptos: data.cryptos || [] });
            setLoading(false);
          }
        } catch (e) { console.debug("WS message parse skipped:", e); }
      };

      ws.onclose = () => {
        if (!isActiveRef.current) return;
        failureCountRef.current += 1;
        if (failureCountRef.current >= MAX_WS_FAILURES) {
          // Give up on WS, fall back to REST polling permanently for this session
          startRestPolling();
          return;
        }
        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(delay * 2, 60000);
        wsReconnectRef.current = setTimeout(connectWebSocket, delay);
      };

      // Swallow the error event — onclose drives reconnect logic.
      ws.onerror = () => { /* handled via onclose */ };
      wsRef.current = ws;
    } catch (e) {
      // WebSocket constructor itself threw — fall back to polling immediately
      startRestPolling();
    }
  }, [startRestPolling]);

  useEffect(() => {
    isActiveRef.current = true;
    fetchMarketData();
    connectWebSocket();
    return () => {
      isActiveRef.current = false;
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
      }
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [fetchMarketData, connectWebSocket]);

  return { marketData, loading, priceTicks };
}

export function useWatchlist(isAuthenticated) {
  const { authHeaders } = useAuth();
  const [watchlist, setWatchlist] = useState([]);

  const fetchWatchlist = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/watchlist`, { headers });
      setWatchlist(response.data.symbols || []);
    } catch (error) {
      console.error("Failed to fetch watchlist:", error);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (isAuthenticated) fetchWatchlist();
  }, [isAuthenticated, fetchWatchlist]);

  const toggleWatchlist = useCallback(async (symbol, e) => {
    e.stopPropagation();
    const headers = authHeaders();
    const isWatched = watchlist.includes(symbol);
    try {
      const endpoint = isWatched ? "remove" : "add";
      const response = await axios.post(
        `${API}/watchlist/${endpoint}`,
        { symbol },
        { headers }
      );
      setWatchlist(response.data.symbols || []);
      toast.success(isWatched ? `${symbol} removed from watchlist` : `${symbol} added to watchlist`);
    } catch (error) {
      toast.error("Failed to update watchlist");
    }
  }, [watchlist, authHeaders]);

  return { watchlist, toggleWatchlist };
}

export function useSignalAlerts(isAuthenticated) {
  const { authHeaders } = useAuth();
  const [signalAlerts, setSignalAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchSignalAlerts = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/signal-alerts`, { headers });
      setSignalAlerts(response.data.alerts || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch signal alerts:", error);
    }
  }, [authHeaders]);

  const checkNewAlerts = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.post(`${API}/signal-alerts/check`, {}, { headers });
      if (response.data.count > 0) {
        fetchSignalAlerts();
        playAlertSound();
        response.data.new_alerts.slice(0, 2).forEach(alert => {
          const label = alert.alert_type === "STRONG_BUY" ? "STRONG BUY" : alert.alert_type === "STRONG_SELL" ? "STRONG SELL" : "ENTRY HIT";
          toast.info(`${label}: ${alert.symbol} @ $${alert.current_price}`);
        });
      }
    } catch (error) {
      console.error("checkNewAlerts failed:", error);
    }
  }, [fetchSignalAlerts, authHeaders]);

  const markAlertsRead = useCallback(async (alertIds = []) => {
    try {
      const headers = authHeaders();
      await axios.post(`${API}/signal-alerts/mark-read`, { alert_ids: alertIds }, { headers });
      fetchSignalAlerts();
    } catch (error) {
      console.error("markAlertsRead failed:", error);
    }
  }, [fetchSignalAlerts, authHeaders]);

  const dismissAlert = useCallback(async (alertId) => {
    try {
      const headers = authHeaders();
      await axios.delete(`${API}/signal-alerts/${alertId}`, { headers });
      fetchSignalAlerts();
    } catch (error) {
      console.error("dismissAlert failed:", error);
    }
  }, [fetchSignalAlerts, authHeaders]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSignalAlerts();
      checkNewAlerts();
    }
    const interval = setInterval(() => {
      if (isAuthenticated) checkNewAlerts();
    }, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchSignalAlerts, checkNewAlerts]);

  return { signalAlerts, unreadCount, markAlertsRead, dismissAlert };
}

export function useSearch(isAuthenticated, setAuthDialog, setPaymentDialog) {
  const { authHeaders } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    if (!isAuthenticated) {
      setAuthDialog(true);
      return;
    }
    setSearchLoading(true);
    setSearchResult(null);
    try {
      const headers = authHeaders();
      const response = await axios.get(`${API}/search/query?q=${encodeURIComponent(searchQuery.trim())}`, { headers });
      setSearchResult(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(error.response.data.detail || "Asset not found");
      } else if (error.response?.status === 402) {
        toast.error("Subscribe to get unlimited predictions");
        setPaymentDialog(true);
      } else {
        toast.error("Search failed. Try a valid ticker.");
      }
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, isAuthenticated, setAuthDialog, setPaymentDialog, authHeaders]);

  return { searchQuery, setSearchQuery, searchResult, setSearchResult, searchLoading, handleSearch };
}
